import React, { useState, useEffect, useCallback } from 'react';
import { Lock, Eye, EyeOff, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import PhoneInput from './PhoneInput';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SS_KEY = 'webAuthenticated';
const SS_INV = 'webAuthInvitado';

/**
 * Devuelve true si el usuario YA tiene sesión válida en sessionStorage.
 * Se reusa la misma clave que WebAuthGuard para que un login en cualquier
 * sitio sirva para todo (modo privada y modo ver_libre).
 */
export const isWebAuthenticated = () => {
  try { return sessionStorage.getItem(SS_KEY) === 'true'; } catch { return false; }
};

export const getAuthInvitado = () => {
  try { return JSON.parse(sessionStorage.getItem(SS_INV) || 'null'); } catch { return null; }
};

/**
 * Registra el acceso a una sección (sólo si el backend reporta modo individual).
 * Es "fire and forget": no bloquea la UI ni muestra errores.
 */
export const logAcceso = async (seccion) => {
  try {
    const inv = getAuthInvitado();
    if (!inv) return;
    await fetch(`${API_URL}/api/web/auth/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        seccion,
        invitado_id: inv.id || '',
        telefono: inv.telefono || '',
        nombre: inv.nombre || '',
      }),
    });
  } catch {
    /* ignorar */
  }
};

/**
 * Envuelve el contenido de una sección protegida.
 * - Si el modo es 'libre' o 'privada' (esta última ya está cubierta por WebAuthGuard a nivel raíz),
 *   simplemente renderiza children.
 * - Si el modo es 'ver_libre' (datos protegidos), exige login antes de mostrar.
 */
const ProtectedSectionGuard = ({ seccion, children }) => {
  const [authStatus, setAuthStatus] = useState(null); // null | {modo_acceso, modo_login}
  const [isAuthed, setIsAuthed] = useState(isWebAuthenticated());
  const [creds, setCreds] = useState({ username: '', telefono: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/web/auth/status`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setAuthStatus(data);
      } catch {
        if (!cancelled) setAuthStatus({ modo_acceso: 'libre' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const registrar = useCallback(async () => {
    await logAcceso(seccion);
  }, [seccion]);

  // Si ya está autenticado y el modo es ver_libre, registrar acceso
  useEffect(() => {
    if (!authStatus) return;
    if (authStatus.modo_acceso === 'ver_libre' && isAuthed) registrar();
  }, [authStatus, isAuthed, registrar]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const body = authStatus?.modo_login === 'individual'
        ? { telefono: creds.telefono, password: creds.password }
        : { username: creds.username, password: creds.password };
      const res = await fetch(`${API_URL}/api/web/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        sessionStorage.setItem(SS_KEY, 'true');
        if (data.invitado) sessionStorage.setItem(SS_INV, JSON.stringify(data.invitado));
        setIsAuthed(true);
        await logAcceso(seccion);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Credenciales incorrectas');
      }
    } catch {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Mientras carga el estado, no renderizar nada (evita parpadeo)
  if (!authStatus) return null;

  // Si el modo NO es 'ver_libre', no aplicamos gate aquí (lo cubre WebAuthGuard a nivel raíz).
  if (authStatus.modo_acceso !== 'ver_libre') return children;

  // Modo ver_libre + ya autenticado
  if (isAuthed) return children;

  const isIndividual = authStatus.modo_login === 'individual';

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#FDFCFA' }}>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8" data-testid="seccion-protegida-login">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: '#D4846D20' }}>
              <Lock className="w-10 h-10" style={{ color: '#D4846D' }} />
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-center mb-2" style={{ color: '#5C5C5C', fontFamily: 'Playfair Display' }}>
            Acceso protegido
          </h1>
          <p className="text-gray-500 text-center mb-8">
            Esta sección requiere que te identifiques antes de continuar.
          </p>
          <form onSubmit={handleLogin} className="space-y-4">
            {isIndividual ? (
              <PhoneInput
                value={creds.telefono}
                onChange={(v) => setCreds({ ...creds, telefono: v })}
                label="Teléfono"
                placeholder="Tu número de teléfono"
                required
                testIdSuffix="seccion"
              />
            ) : (
              <div>
                <Label htmlFor="username">Usuario</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Introduce el usuario"
                  value={creds.username}
                  onChange={(e) => setCreds({ ...creds, username: e.target.value })}
                  className="mt-1"
                  required
                  data-testid="seccion-input-usuario"
                />
              </div>
            )}
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPwd ? 'text' : 'password'}
                  placeholder="Introduce la contraseña"
                  value={creds.password}
                  onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                  className="pr-10"
                  required
                  data-testid="seccion-input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {error && <p className="text-red-500 text-sm text-center" data-testid="seccion-login-error">{error}</p>}
            <Button
              type="submit"
              disabled={loading}
              className="w-full text-white py-6"
              style={{ backgroundColor: '#D4846D' }}
              data-testid="seccion-btn-login"
            >
              {loading ? 'Verificando…' : 'Entrar'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProtectedSectionGuard;
